import React, { useContext } from 'react';
import { Dialog, DialogContent, Box, Typography, IconButton, Fade } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import { DataContext } from '../data/DataContext';
import { DataContextType } from '../data_types/interfaces';
import { StreakMeta } from '../data_types/types';
import { formatHistoryDateVerbose } from '../data/formatting';
import { DIVISION_BADGES, FINISHES_POSITIVE } from '../data/const';
import { DIVISION_NAMES, describeCurrentStreak, getRecentRatingChange } from '../data/player_insights';
import { useRoundWindow } from '../shared/RoundWindow';
import { DS, Overline, enterSx, formatSigned, numericSx, trendColor } from '../shared/Dashboard';

interface MetricTileProps {
    label: string;
    value: React.ReactNode;
    sub?: React.ReactNode;
    accent?: string;
    delay?: number;
}

const MetricTile: React.FC<MetricTileProps> = ({ label, value, sub, accent, delay = 0 }) => (
    <Box sx={{
        minWidth: 0,
        p: 2,
        borderRadius: '12px',
        background: DS.inset,
        border: `1px solid ${DS.border}`,
        borderTop: accent ? `2px solid ${accent}` : `1px solid ${DS.border}`,
        ...enterSx(delay),
    }}>
        <Overline>{label}</Overline>
        <Typography sx={{ fontSize: '22px', fontWeight: 800, lineHeight: 1.25, mt: 0.5, color: DS.text, ...numericSx }}>
            {value}
        </Typography>
        {sub && (
            <Typography sx={{ fontSize: '12px', color: DS.textMuted, mt: 0.25, lineHeight: 1.35, ...numericSx }}>
                {sub}
            </Typography>
        )}
    </Box>
);

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <Box sx={{ '& + &': { mt: 3, pt: 3, borderTop: `1px solid ${DS.border}` } }}>
        <Typography sx={{ fontSize: '13px', fontWeight: 700, color: DS.text, mb: 1.5 }}>{title}</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 1.5 }}>
            {children}
        </Box>
    </Box>
);

const rounds = (n: number) => `${n} round${n !== 1 ? 's' : ''}`;

const streakRange = (streak: StreakMeta) =>
    streak.len > 0 ? `#${streak.start_match_num}–#${streak.end_match_num}` : undefined;

interface Props {
    open: boolean;
    onClose: () => void;
}

const PlayerSummaryDialog: React.FC<Props> = ({ open, onClose }) => {
    const context = useContext<DataContextType | undefined>(DataContext);
    const { summaryData, playerData, playerSeasonData, historyData } = context || {};
    const { roundWindow } = useRoundWindow();
    if (!summaryData || !playerData) return null;

    const division: number = playerSeasonData.division;
    const recentChange = getRecentRatingChange(historyData ?? [], roundWindow);
    const eloPerRound: number = summaryData.mean_points_gained_per_round;
    const gainedPercent: number = summaryData.grouped_rating_changes?.[FINISHES_POSITIVE]?.percent ?? 0;
    const mostInDay = summaryData.most_rounds_for_day;
    const currentStreak: StreakMeta | undefined = summaryData.current_streak;
    const bestStreak: StreakMeta | undefined = summaryData.longest_green_streak;
    const worstStreak: StreakMeta | undefined = summaryData.longest_red_streak;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            TransitionComponent={Fade}
            TransitionProps={{ timeout: 250 }}
            PaperProps={{
                sx: {
                    background: DS.surface,
                    backgroundImage: 'none',
                    border: `1px solid ${DS.border}`,
                    borderRadius: DS.radius,
                    m: { xs: 1.5, sm: 4 },
                    width: { xs: 'calc(100% - 24px)', sm: undefined },
                },
            }}
        >
            <DialogContent sx={{ p: 0 }}>
                {/* Header */}
                <Box sx={{
                    px: { xs: 2, sm: 3 },
                    pt: { xs: 2, sm: 3 },
                    pb: 2.5,
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 2,
                    borderBottom: `1px solid ${DS.border}`,
                }}>
                    <Box sx={{ minWidth: 0 }}>
                        <Overline>Season summary</Overline>
                        <Typography sx={{ fontSize: '22px', fontWeight: 800, letterSpacing: '-0.02em', color: DS.text }}>
                            {playerData.playerSettings.displayName}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap', color: DS.textMuted, fontSize: '13px' }}>
                            {division >= 0 && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Box component="img" src={DIVISION_BADGES[division]} alt="" sx={{ width: 18, height: 18 }} />
                                    <span>{DIVISION_NAMES[division]} Division</span>
                                </Box>
                            )}
                            <span>·</span>
                            <span>Level {playerData.playerStats.playerLevel}</span>
                        </Box>
                    </Box>
                    <IconButton onClick={onClose} aria-label="Close" sx={{ color: DS.textMuted, '&:hover': { color: DS.text } }}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Box sx={{ px: { xs: 2, sm: 3 }, pt: 3, pb: { xs: 3, sm: 4 } }}>
                    <Section title="Rating">
                        <MetricTile delay={0.03} label="Current rating" value={summaryData.current_rating} />
                        <MetricTile delay={0.06} label="Season peak" value={summaryData.highest_rating} accent={DS.gold} />
                        <MetricTile
                            delay={0.09}
                            label={`Last ${roundWindow} rounds`}
                            value={<span style={{ color: trendColor(recentChange) }}>{formatSigned(recentChange)}</span>}
                            sub="rating change"
                        />
                        <MetricTile delay={0.12} label="Rounds gaining Elo" value={`${gainedPercent}%`} sub="rating went up" />
                        <MetricTile
                            delay={0.15}
                            label="Elo per round"
                            value={<span style={{ color: trendColor(eloPerRound) }}>{formatSigned(eloPerRound, 2)}</span>}
                            sub="season average"
                        />
                    </Section>

                    <Section title="Activity">
                        <MetricTile delay={0.18} label="Total rounds" value={summaryData.total_rounds} />
                        <MetricTile delay={0.21} label="Rounds per day" value={summaryData.rounds_per_day} sub="since first round" />
                        <MetricTile
                            delay={0.24}
                            label="Most in a day"
                            value={mostInDay ? mostInDay[1].length : 0}
                            sub={mostInDay ? formatHistoryDateVerbose(mostInDay[1][0]?.date) : undefined}
                        />
                        <MetricTile
                            delay={0.27}
                            label="Last round"
                            value={<Box component="span" sx={{ fontSize: '17px' }}>{formatHistoryDateVerbose(summaryData.last_round)}</Box>}
                        />
                    </Section>

                    <Section title="Streaks">
                        {currentStreak && (
                            <MetricTile
                                delay={0.3}
                                label={`Current · ${describeCurrentStreak(currentStreak.points_gained, currentStreak.includesZero)}`}
                                value={rounds(currentStreak.len)}
                                sub={<span style={{ color: trendColor(currentStreak.points_gained) }}>{formatSigned(currentStreak.points_gained)} pts</span>}
                                accent={currentStreak.points_gained >= 0 ? DS.gain : DS.loss}
                            />
                        )}
                        {bestStreak && (
                            <MetricTile
                                delay={0.33}
                                label="Longest climb"
                                value={rounds(bestStreak.len)}
                                sub={<>{formatSigned(bestStreak.points_gained)} pts {streakRange(bestStreak) && `· ${streakRange(bestStreak)}`}</>}
                                accent={DS.gain}
                            />
                        )}
                        {worstStreak && (
                            <MetricTile
                                delay={0.36}
                                label="Longest slide"
                                value={rounds(worstStreak.len)}
                                sub={<>{formatSigned(worstStreak.points_gained)} pts {streakRange(worstStreak) && `· ${streakRange(worstStreak)}`}</>}
                                accent={DS.loss}
                            />
                        )}
                        {summaryData.bounce_back_percentage !== undefined && (
                            <MetricTile
                                delay={0.39}
                                label="Bounce-back rate"
                                value={`${summaryData.bounce_back_percentage}%`}
                                sub="rating didn't drop again after a drop"
                                accent={DS.info}
                            />
                        )}
                    </Section>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default PlayerSummaryDialog;
